import { ChangeEventHandler, useCallback, useState } from "react";
import { MagnifyingGlassIcon, ArrowLeftIcon } from "@radix-ui/react-icons";
import { Box, Flex, TextField, Button, Switch, Spinner, Tooltip } from "@radix-ui/themes";
import { PiTranslate } from "react-icons/pi";

import { useDebouncedCallback } from "@/lib/useDebouncedCallback";
import { TicketTranscriptFilterValue } from "@/api/ticket-transcript/TicketTranscript.model";

import { TicketTranscriptFilterMenu } from "./TicketTranscriptFilterMenu";
import styles from "./TicketTranscript.module.scss";

interface TicketTranscriptHeaderProps {
  search: string;
  filterValue: TicketTranscriptFilterValue;
  onSearchChange: (newValue: string) => void;
  onFilterChange: (newValue: TicketTranscriptFilterValue) => void;
  onBack?: () => void;
  translateAll?: boolean;
  onTranslateAll?: (enabled: boolean) => Promise<void>;
  isBatchTranslating?: boolean;
}

export function TicketTranscriptHeader({
  search,
  filterValue,
  onSearchChange,
  onFilterChange,
  onBack,
  translateAll = false,
  onTranslateAll,
  isBatchTranslating = false,
}: TicketTranscriptHeaderProps) {
  const debouncedOnSearchChange = useDebouncedCallback<void, [string]>(
    onSearchChange,
    700
  );

  const [innerSearch, setInnerSearch] = useState(search);

  const handleSearchChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (event) => {
      setInnerSearch(event.currentTarget.value);
      debouncedOnSearchChange(event.currentTarget.value);
    },
    [debouncedOnSearchChange]
  );

  const handleBackClick = useCallback(() => {
    onBack?.();
  }, [onBack]);

  return (
    <Flex direction={"column"} gap={"2"}>
      <Flex px={"3"} align={"center"} className={styles.Search}>
        {onBack && (
          <Button
            variant="ghost"
            size="2"
            onClick={handleBackClick}
            style={{ marginRight: "8px" }}
          >
            <ArrowLeftIcon width="16" height="16" />
            Back
          </Button>
        )}
        <Box width={"100%"}>
          <TextField.Root
            placeholder={"Search in transcript…"}
            color={"gray"}
            size={"2"}
            value={innerSearch}
            onChange={handleSearchChange}
            className={styles.TextField}
          >
            <TextField.Slot>
              <MagnifyingGlassIcon height={"16"} width={"16"} />
            </TextField.Slot>

            <TextField.Slot pr="3">
              <TicketTranscriptFilterMenu
                filterValue={filterValue}
                onFilterChange={onFilterChange}
              />
            </TextField.Slot>
          </TextField.Root>
        </Box>
      </Flex>

      {onTranslateAll && (
        <Flex
          px={"3"}
          pb={"2"}
          align={"center"}
          gap={"2"}
          className={styles.TranslateHeader}
        >
          <PiTranslate style={{ color: "var(--gray-11)", fontSize: "18px" }} />
          <span style={{ fontSize: "14px", fontWeight: 500, color: "var(--gray-12)" }}>
            Translate All
          </span>
          <Switch
            size="2"
            disabled={isBatchTranslating}
            checked={translateAll}
            onCheckedChange={onTranslateAll}
          />
          {isBatchTranslating && <Spinner />}
        </Flex>
      )}
    </Flex>
  );
}
